import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { isNullOrUndefined } from '@uzurv/core';
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Driver } from '../../models';
import { Person } from '../../models/person';

@Injectable({
  providedIn: 'root'
})
export class PersonService extends UzurvRestService {
  readonly resourceUrl = 'persons/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Search a person by arbitrary means. Will match on name, username, ID, etc.
   *
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @permission driver_managment
   */
  searchPerson(searchString: string, pageNumber = 0, pageSize = 20): Observable<Array<Person>> {
    return this.get(`search/${this.resourceUrl}`, {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Person)));
  }

  /**
   * Updates an existing person
   *
   * @param personId
   * @param person
   * @permission person_update
   */
  updatePersonWithId(personId: number, person: Person): Observable<Person> {
    return this.put<Person>(`${this.resourceUrl}${personId}`, Serialize(person)).pipe(map(response => Deserialize(response, Person)));
  }

  /**
   * Returns a given person's referrer
   *
   * @param personId
   * @permission driver_management
   */
  findReferrerForPersonWithId(personId: number): Observable<Person> {
    return this.get<Person>(`${this.resourceUrl}${personId}/referrer/`).pipe(map(response => Deserialize(response, Person)));
  }

  /**
   * Sets a person's referrer
   *
   * @param personId
   * @param referrerEmailAddress
   * @permission driver_management
   */
  setReferrerForPersonWithId(personId: number, referrerPersonId: number): Observable<Person> {
    return this.put<Person>(`${this.resourceUrl}${personId}/referrer/?referrerPersonId=${referrerPersonId}`, null).pipe(
      map(response => Deserialize(response, Person))
    );
  }

  /**
   * Combines first and last name
   * ex: Brandon Tate
   *
   * @param user
   */
  getFullName(person: Person): string {
    if (isNullOrUndefined(person)) {
      return '';
    }

    return person.firstName + ' ' + person.lastName;
  }

  updateDriver(driverId: number, driver: Driver): Observable<Driver> {
    return this.patch<Driver>(`${this.resourceUrl}${driverId}/driver`, driver).pipe(map(response => Deserialize(response, Driver)));
  }
}
